import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatStepper } from "@angular/material/stepper";
import { IFormArray, IFormBuilder, IFormGroup } from "@rxweb/types";
import { CreatePayablesModalService } from "app/components/v1/create-payables-modal/create-payables-modal.service";
import { IPayablesFormArray } from "app/components/v1/create-payables-modal/interfaces";
import { EnvioNotasModalService } from "app/components/v1/xml-modal/envio-notas-modal.service";
import { UserService } from "app/core/user/user.service";
import { ClientesService } from "app/modules/admin/v1/cedente/cedente.service";
import { Payable } from "app/modules/admin/v1/operacoes/operacoes.types";
import { CreatePayerModalComponent } from "app/modules/admin/v1/payer/payer-create/payer-create-modal/payer-create-modal.component";
import { validateCmc7 } from "app/utils/cmc7-validator";
import { Cedente, Sacado } from "generated/graphql";
import moment from "moment";
import { NgxDropzoneChangeEvent } from "ngx-dropzone";
import { combineLatest, Observable, of, Subject } from "rxjs";
import { debounceTime, map, startWith, switchMap, takeUntil, tap } from "rxjs/operators";
import { UploadBankCheckModalService } from "./upload-bank-check-modal.service";
import { DATE_FORMAT_REGEX } from "app/shared/constants/date-format-regex.constant";
import { adjustDateToEndOfDay } from "app/utils/adjust-date-to-end-of-day";

@Component({
    templateUrl: "./upload-bank-check-modal.component.html",
    selector: "UploadBankCheckModal",
    styleUrls: ["./upload-bank-check-modal.component.scss"],
})
export class UploadBankCheckModalComponent implements OnInit, OnDestroy {
    @ViewChild("stepper") stepper: MatStepper;
    private unsubscribeAll = new Subject();

    public total = 0;
    public loading = false;
    public disabled = false;
    public dataSource = [];
    public assignor: Partial<Cedente>;
    public minDate = new Date();
    public createPayableForm: IFormGroup<any>;
    public installments: IFormArray<any>;
    public formBuilder: IFormBuilder;
    public animation: boolean = true;
    public payables = [];
    public payers = [];
    public filteredPayers$: Observable<any>;
    public payableType: string;
    public hasCmc7 = false;
    public installmentForm: FormGroup;
    public selectedPayer = "";
    public files: File[] = [];

    constructor(
        public dialog: MatDialog,
        formBuilder: FormBuilder,
        private snack: MatSnackBar,
        private uploadBankService: UploadBankCheckModalService,
        private userService: UserService,
        private cedenteService: ClientesService,
        private sendNFsModalService: EnvioNotasModalService,
        private createPayablesModalService: CreatePayablesModalService,
        @Inject(MAT_DIALOG_DATA) public data: { assignor: Cedente; payableType: string },
    ) {
        this.formBuilder = formBuilder;
    }

    ngOnInit() {
        this.assignor = this.data.assignor;
        this.payableType = this.data.payableType;
        this.hasCmc7 = this.payableType === "CHEQUE";

        this.sendNFsModalService.getPayables$.subscribe((payables) => (this.payables = payables));

        this.sendNFsModalService.getUpdatePayables$.subscribe((id) => {
            this.addSelectedPayableId(false, { id });
        });

        this.createPayableForm = this.formBuilder.group<{
            minibanco: string;
            cedente: string;
            sacado: Sacado;
            parcelas: IPayablesFormArray[];
        }>({
            minibanco: [{ value: this.userService.user$.minibanco.razaoSocial, disabled: true }, Validators.required],
            cedente: [{ value: this.assignor.razaoSocial, disabled: true }, Validators.required],
            sacado: [null, Validators.required],
            parcelas: this.formBuilder.array<IPayablesFormArray>([]),
        });

        this.sumInstallments();
        this.watchPayerAutocomplete();
        this.addInstallment();
    }

    onSelect(event: NgxDropzoneChangeEvent) {
        this.files.push(...event.addedFiles);
    }

    onRemove(event: File) {
        this.files.splice(this.files.indexOf(event), 1);
    }

    addSelectedPayableId(event, parcela: Partial<Payable>) {
        if (event) {
            this.sendNFsModalService.setPayables = [...this.payables, parcela];

            return;
        }

        this.sendNFsModalService.setPayables = this.payables.filter((payables) => payables.id !== parcela.id);
    }

    get installmentsFormArray() {
        return this.createPayableForm.get("parcelas") as FormArray;
    }

    watchPayerAutocomplete() {
        this.cedenteService.sacados$.subscribe((res) => {
            this.payers = res;
        });

        this.filteredPayers$ = this.createPayableForm.get("sacado").valueChanges.pipe(
            takeUntil(this.unsubscribeAll),
            startWith(""),
            debounceTime(300),
            switchMap((value) => {
                if (value !== "") {
                    return this.cedenteService
                        .searchSacados(this.cleanValueString(value).toLowerCase())
                        .pipe(map((result) => result.data));
                } else {
                    return of(this.payers);
                }
            }),
        );
    }

    cleanValueString(value: number | string): string {
        return String(value).replaceAll(/[-./]/g, "");
    }

    sumInstallments() {
        this.createPayableForm
            .get("parcelas")
            .valueChanges.pipe(takeUntil(this.unsubscribeAll))
            .subscribe((installments: { valorDaParcela: string; vencimento: Date }[]) => {
                this.total = installments.reduce((acc, curr) => Number(acc) + Number(curr.valorDaParcela), 0);
            });
    }

    deleteInstallment(index: number) {
        this.installmentsFormArray.removeAt(index);
    }

    addInstallment(values?: Partial<IPayablesFormArray>) {
        this.installmentForm = this.formBuilder.group({
            valorDaParcela: [
                values.valorDaParcela || 0,
                [Validators.required, Validators.min(0.01), Validators.max(1_000_000_000)],
            ],
            vencimento: [values.vencimento || "", Validators.required],
            payableNumber: [values.payableNumber || "", Validators.required],
            cmc7: [values.cmc7 || "", this.hasCmc7 ? [Validators.required, validateCmc7] : null],
        });

        this.installmentsFormArray.push(this.installmentForm);
    }

    displayName(value): string {
        return value && value.razaoSocial;
    }

    newPayer() {
        const addPayerDialog = this.dialog.open(CreatePayerModalComponent, {
            width: "80vw",
            height: "80vh",
            id: "dialog-new-payer",
        });
        addPayerDialog.componentInstance.cancelEmitter.pipe(takeUntil(this.unsubscribeAll)).subscribe(() => {
            addPayerDialog.close();
        });
        addPayerDialog.componentInstance.afterSaveEmitter.pipe(takeUntil(this.unsubscribeAll)).subscribe(() => {
            addPayerDialog.close();
        });
        addPayerDialog.componentInstance.createdPayerEmitter
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((createdPayer) => {
                this.createPayableForm.controls.sacado.setValue(createdPayer);
            });
    }

    onPayerSelected(event: MatAutocompleteSelectedEvent) {
        const selectedPayer = event.option.value;

        this.selectedPayer = selectedPayer.razaoSocial || selectedPayer.nomeFantasia;
        this.createPayableForm.controls.sacado.setValue(selectedPayer);
    }

    onBlurPayerInput() {
        const selectedPayer = this.createPayableForm.controls.sacado.value;

        if (typeof selectedPayer === "string") {
            this.createPayableForm.controls.sacado.setValue(null);
        }
    }

    async uploadCMC7() {
        if (this.files.length === 0) return;

        this.installmentsFormArray.controls = [];

        this.loading = true;

        const result = await Promise.all(this.files.map((file) => this.uploadBankService.uploadCMC7(file)));

        this.loading = false;

        result.forEach((result) => {
            this.addInstallment({ cmc7: result.cmc7 });
        });
        this.stepper.next();
    }

    createPayable() {
        if (this.createPayableForm.invalid) {
            this.snack.open("Preencha todos os campos corretamente !", "X", {
                duration: 3000,
            });

            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const payablesCreated = this.installmentsFormArray.value.map((recebivel: IPayablesFormArray) => {
            const { sacado } = this.createPayableForm.value;
            const { valorDaParcela, vencimento, payableNumber, cmc7 } = recebivel;

            const data = {
                cedenteId: this.assignor.id,
                sacadoId: sacado.id,
                minibancoId: this.userService.user$.minibanco.id,
                dtVencimento: vencimento,
                dtEmissao: new Date(),
                payableType: this.data.payableType,
                valor: parseFloat(valorDaParcela),
                payable_number: payableNumber,
                cmc7,
            };

            return this.uploadBankService.createPayable(data).pipe(tap((res) => res));
        });

        combineLatest(payablesCreated)
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(
                (res) => {
                    this.loading = false;

                    const partialPayableInfo = res.map((payable: Payable) => {
                        const {
                            sacado: { razaoSocial, document },
                            valor,
                            dtVencimento,
                            id,
                            sacadoId,
                        } = payable;

                        const dueDateFormatted = DATE_FORMAT_REGEX.test(dtVencimento as string)
                            ? String(dtVencimento).split("/").reverse().join("-")
                            : dtVencimento;

                        return {
                            id,
                            sacado: { razaoSocial, document, id: sacadoId },
                            sacadoId,
                            valor,
                            dtVencimento: moment(adjustDateToEndOfDay(new Date(dueDateFormatted).toISOString())).format(
                                "DD/MM/YYYY",
                            ),
                        };
                    });

                    this.sendNFsModalService.setPayables = [...this.payables, ...partialPayableInfo];

                    this.snack.open(
                        `${payablesCreated.length > 1 ? "Recebíveis criados" : "Recebível criado"} com sucesso !`,
                        "X",
                        { panelClass: "snack-bar-success", duration: 5000 },
                    );

                    this.dialog.closeAll();
                },
                (err) => {
                    this.loading = false;

                    this.snack.open(err.error.message, "Ok", {
                        panelClass: ["mat-toolbar", "mat-warn"],
                        duration: 3500,
                    });
                },
            );
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }
}
